import { useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError } from "@/api/client";
import {
  useDesk,
  useDeskKeepalive,
  useSessions,
  useWakeDesk,
} from "@/api/hooks";
import { deriveDeskState } from "@/api/types";
import { DeskBottomBar } from "@/components/desk/desk-bottom-bar";
import {
  DeployingScreen,
  DeskErrorScreen,
  DeskLoadingSkeleton,
} from "@/components/desk/desk-lifecycle-screens";
import { DeskRail, type RailPane } from "@/components/desk/desk-rail";
import { DeskTopBar } from "@/components/desk/desk-top-bar";
import { FilesPanel } from "@/components/desk/files-panel";
import { FileViewer } from "@/components/desk/file-viewer";
import { MobileOverlay } from "@/components/desk/mobile-overlay";
import { SettingsPanel } from "@/components/desk/settings-panel";
import {
  type OpenSlot,
  SessionGrid,
} from "@/components/desk/session/session-grid";
import { SessionTabBar } from "@/components/desk/session/session-tab-bar";
import { useIsMobile } from "@/hooks/use-media-query";

const MAX_SLOTS = 6;

interface OpenFile {
  path: string;
  name: string;
  root: "data" | "sprite";
}

function slotKey(): string {
  return `slot-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

export function DeskDetail() {
  const { deskId } = useParams({ from: "/desk/$deskId" });
  const { data, isLoading, error, refetch } = useDesk(deskId);
  const wakeMutation = useWakeDesk();
  const [wakeFailed, setWakeFailed] = useState(false);

  const desk = data?.desk;
  const state = desk ? deriveDeskState(desk) : null;
  const isActive = state === "active";

  const keepalive = useDeskKeepalive(deskId, desk?.relay_url ?? null, isActive);

  const { data: sessions } = useSessions(deskId);
  const hasActiveWork = !!sessions?.some(
    (s) => s.agentStatus === "running" || s.agentStatus === "starting",
  );
  const liveSpend =
    sessions?.reduce((sum, s) => sum + (s.totalCost ?? 0), 0) ?? 0;

  const handleWake = useCallback(() => {
    if (!desk) return;
    setWakeFailed(false);
    wakeMutation.mutate(desk.id, {
      onError: () => setWakeFailed(true),
      onSuccess: () => setWakeFailed(false),
    });
  }, [desk, wakeMutation]);

  const [wokeDeskId, setWokeDeskId] = useState<string | null>(null);
  useEffect(() => {
    if (!desk) return;
    if (state !== "stored" && state !== "warm") return;
    if (wakeMutation.isPending) return;
    if (wokeDeskId === desk.id) return;
    setWokeDeskId(desk.id);
    handleWake();
  }, [desk, state, wakeMutation.isPending, wokeDeskId, handleWake]);

  // Debounce so Wake button doesn't flicker on transient polling blips.
  const [stableInactiveState, setStableInactiveState] = useState(false);
  useEffect(() => {
    if (state === "active" || state === "waking" || state === "booting") {
      setStableInactiveState(false);
      return;
    }
    if (wakeMutation.isPending) {
      setStableInactiveState(false);
      return;
    }
    const id = setTimeout(() => setStableInactiveState(true), 2500);
    return () => clearTimeout(id);
  }, [state, wakeMutation.isPending]);

  // --- Panel & file viewer state ---
  const isMobile = useIsMobile();
  // Desktop: panels dock in-line. Mobile: pane state controls which overlay
  // is open (or null = closed). We use the same state var for both; the
  // layout reads it differently.
  const [railPane, setRailPane] = useState<RailPane>(isMobile ? null : "files");
  const [openFile, setOpenFile] = useState<OpenFile | null>(null);

  const handleFileOpen = useCallback(
    (path: string, name: string, root: "data" | "sprite") => {
      setOpenFile({ path, name, root });
    },
    [],
  );

  const handleFileOpenFromSession = useCallback(
    (path: string, name: string) => {
      setOpenFile({ path, name, root: "data" });
    },
    [],
  );

  // --- Multi-tile session state ---
  const [slots, setSlots] = useState<OpenSlot[]>([]);
  const [focusedKey, setFocusedKey] = useState<string | null>(null);

  const openSessionIds = useMemo(() => {
    const s = new Set<string>();
    for (const slot of slots) if (slot.sessionId) s.add(slot.sessionId);
    return s;
  }, [slots]);

  // On first sessions load, auto-open the most recent one in a tile.
  const [initialOpened, setInitialOpened] = useState(false);
  useEffect(() => {
    if (initialOpened) return;
    if (!sessions) return;
    if (sessions.length === 0) {
      // No sessions yet — start with one empty new-session tile so the
      // composer is ready to type into.
      const key = slotKey();
      setSlots([{ key, sessionId: null }]);
      setFocusedKey(key);
      setInitialOpened(true);
      return;
    }
    const sorted = [...sessions].sort((a, b) => {
      const at = a.lastActivityAt ?? a.createdAt;
      const bt = b.lastActivityAt ?? b.createdAt;
      return bt.localeCompare(at);
    });
    const key = slotKey();
    setSlots([{ key, sessionId: sorted[0].sessionId }]);
    setFocusedKey(key);
    setInitialOpened(true);
  }, [sessions, initialOpened]);

  const handleToggleSession = useCallback(
    (sessionId: string) => {
      setSlots((prev) => {
        const existing = prev.find((s) => s.sessionId === sessionId);
        if (existing) {
          // Already open → close it
          if (focusedKey === existing.key) setFocusedKey(null);
          return prev.filter((s) => s.key !== existing.key);
        }
        if (prev.length >= MAX_SLOTS) return prev; // guarded by UI too
        const key = slotKey();
        setFocusedKey(key);
        return [...prev, { key, sessionId }];
      });
    },
    [focusedKey],
  );

  const handleAddNewSession = useCallback(() => {
    setSlots((prev) => {
      if (prev.length >= MAX_SLOTS) return prev;
      const key = slotKey();
      setFocusedKey(key);
      return [...prev, { key, sessionId: null }];
    });
  }, []);

  const handleSessionCreated = useCallback((key: string, sessionId: string) => {
    setSlots((prev) =>
      prev.map((s) => (s.key === key ? { ...s, sessionId } : s)),
    );
  }, []);

  const handleResetSlotToNew = useCallback((key: string) => {
    setSlots((prev) =>
      prev.map((s) => (s.key === key ? { ...s, sessionId: null } : s)),
    );
    setFocusedKey(key);
  }, []);

  const handleCloseSlot = useCallback(
    (key: string) => {
      setSlots((prev) => prev.filter((s) => s.key !== key));
      if (focusedKey === key) setFocusedKey(null);
    },
    [focusedKey],
  );

  // --- Lifecycle branches ---
  if (isLoading) return <DeskLoadingSkeleton />;

  if (error || !desk) {
    const msg =
      error instanceof ApiError && error.status === 404
        ? "This desk doesn't exist or has been deleted."
        : error instanceof Error
          ? error.message
          : "Failed to load desk.";
    return (
      <DeskErrorScreen
        title="Can't load desk"
        message={msg}
        onRetry={() => refetch()}
      />
    );
  }

  if (state === "booting") {
    return (
      <DeployingScreen
        spriteName={desk.sprite_name}
        createdAt={desk.created_at}
      />
    );
  }

  if (state === "error" && !desk.sprite_name) {
    return (
      <DeskErrorScreen
        title="Desk failed to deploy"
        message="The sprite couldn't be created. This is usually transient — try again or contact support if it keeps happening."
        onRetry={() => refetch()}
      />
    );
  }

  // Past the early returns, desk is defined so state is non-null. We
  // re-derive instead of using `state!` so the type narrows cleanly and
  // we don't need a non-null assertion for the <DeskTopBar> prop.
  const deskState = deriveDeskState(desk);
  const waking =
    deskState === "waking" || (wakeMutation.isPending && !wakeFailed);
  const relayReachable = isActive ? (keepalive.data ?? null) : null;
  const filesAutoRefresh = isActive || hasActiveWork;

  return (
    <div className="h-full flex flex-col" style={{ background: "var(--bg)" }}>
      <DeskTopBar
        desk={desk}
        state={deskState}
        spend={liveSpend}
        waking={waking}
        relayReachable={relayReachable}
        onWake={handleWake}
        wakeFailed={wakeFailed}
        stableInactive={stableInactiveState}
        onOpenFiles={() => setRailPane("files")}
        onOpenSettings={() => setRailPane("settings")}
      />

      <div className="flex-1 flex overflow-hidden relative">
        <DeskRail active={railPane} onToggle={setRailPane} />

        {/* DESKTOP: inline side panels, hidden on mobile via md:flex. */}
        {railPane === "files" && (
          <div
            className="hidden md:flex flex-col flex-shrink-0"
            style={{ width: "240px" }}
          >
            <FilesPanel
              deskId={deskId}
              relayUrl={desk.relay_url}
              onFileOpen={handleFileOpen}
              selectedPath={openFile?.path ?? null}
              onCollapse={() => setRailPane(null)}
              autoRefresh={filesAutoRefresh}
            />
          </div>
        )}

        {openFile && (
          <div
            className="hidden md:flex flex-col flex-shrink-0 border-r"
            style={{ width: "480px", borderColor: "var(--blt)" }}
          >
            <FileViewer
              deskId={deskId}
              filePath={openFile.path}
              fileName={openFile.name}
              root={openFile.root}
              onClose={() => setOpenFile(null)}
              onCollapseSibling={
                railPane === "files" ? () => setRailPane(null) : undefined
              }
            />
          </div>
        )}

        {railPane === "settings" && (
          <div
            className="hidden md:flex flex-col flex-shrink-0 border-r"
            style={{ width: "280px", borderColor: "var(--blt)" }}
          >
            <SettingsPanel desk={desk} onClose={() => setRailPane(null)} />
          </div>
        )}

        {/* MOBILE: slide-in overlays. These components short-circuit on
            desktop via md:hidden internally. */}
        <MobileOverlay
          open={isMobile && railPane === "files"}
          onClose={() => setRailPane(null)}
          side="left"
        >
          <FilesPanel
            deskId={deskId}
            relayUrl={desk.relay_url}
            onFileOpen={(p, n, r) => {
              handleFileOpen(p, n, r);
              setRailPane(null); // dismiss drawer when a file is picked
            }}
            selectedPath={openFile?.path ?? null}
            onCollapse={() => setRailPane(null)}
            autoRefresh={filesAutoRefresh}
          />
        </MobileOverlay>

        <MobileOverlay
          open={isMobile && railPane === "settings"}
          onClose={() => setRailPane(null)}
          side="right"
          widthClass="w-[90vw] max-w-[380px]"
        >
          <SettingsPanel desk={desk} onClose={() => setRailPane(null)} />
        </MobileOverlay>

        {/* Mobile file viewer: full-screen modal (overrides the drawer so
            opening a file from inside the files drawer lands here). */}
        {isMobile && openFile && (
          <div
            className="md:hidden fixed inset-0 z-50 flex flex-col"
            style={{ background: "var(--white)" }}
          >
            <FileViewer
              deskId={deskId}
              filePath={openFile.path}
              fileName={openFile.name}
              root={openFile.root}
              onClose={() => setOpenFile(null)}
            />
          </div>
        )}

        <div
          className="flex-1 flex flex-col overflow-hidden"
          style={{ minWidth: 0 }}
        >
          <SessionTabBar
            sessions={sessions}
            openSessionIds={openSessionIds}
            onToggleSession={handleToggleSession}
            onAddNewSession={handleAddNewSession}
            slotsOpen={slots.length}
            maxSlots={MAX_SLOTS}
          />
          <div className="flex-1 overflow-hidden" style={{ minHeight: 0 }}>
            <SessionGrid
              deskId={deskId}
              canAgent={isActive}
              slots={slots}
              focusedKey={focusedKey}
              onFocus={setFocusedKey}
              onSessionCreated={handleSessionCreated}
              onResetSlotToNew={handleResetSlotToNew}
              onCloseSlot={handleCloseSlot}
              onOpenFile={handleFileOpenFromSession}
            />
          </div>
        </div>
      </div>

      <DeskBottomBar
        desk={desk}
        sessions={sessions}
        relayReachable={relayReachable}
        activeWork={hasActiveWork}
      />
    </div>
  );
}
